<template>
  <v-sheet flat border class="pa-4 col-sm-12">
    <p class="text-subtitle-1 pt-3 text-primary mb-3">
      <span class="font-weight-bold">Rooftop Unit (v1)</span>
      - Input values at execution time
    </p>
    <v-row>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.CurrentDateTime"
          :rules="[isDate]"
          variant="outlined"
          density="compact"
          label="Current Date/Time"
          type="datetime-local"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.ReadingDateTime"
          :rules="[isDate]"
          variant="outlined"
          density="compact"
          label="Reading Date/Time"
          type="datetime-local"
          hint="The latest hour of every rooftop unit below; the other 23 hours count back from it."
          persistent-hint
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.DelayInMinutes"
          :rules="[isNumberGreaterThanZero]"
          variant="outlined"
          density="compact"
          label="Delay in Minutes"
        />
      </v-col>
    </v-row>
    <BusinessHoursInput
      v-model:start-time="localInputData.SiteStartTime"
      v-model:end-time="localInputData.SiteEndTime"
    />
    <v-row>
      <v-col sm="4">
        <v-text-field
          v-model="localInputData.Temperature"
          :rules="[isDecimalOrBlank]"
          variant="outlined"
          density="compact"
          label="Outside Temperature"
        />
      </v-col>
    </v-row>

    <v-divider class="my-4" />

    <p class="text-subtitle-2 text-primary font-weight-bold mb-3">
      Rooftop Unit Hourly Statuses
    </p>

    <p v-if="!endUses.length" class="text-body-2 text-medium-emphasis mb-0">
      No End Uses are available for this site.
    </p>
    <p
      v-else-if="!rtus.length"
      class="text-body-2 text-medium-emphasis mb-0"
      data-testid="rtu-panels-empty"
    >
      No rooftop units yet. Click "Add rooftop unit" to enter 24 hours of kW,
      swings, runtime, starts and shutdowns for one End Use.
    </p>

    <v-sheet
      v-for="rtu in rtus"
      :key="rtu.key"
      flat
      border
      rounded
      class="pa-3 mb-4"
      data-testid="rtu-panel"
    >
      <v-row align="center" dense>
        <v-col cols="12" sm="6">
          <v-autocomplete
            v-model="rtu.endUseId"
            :items="optionsForRtu(rtu)"
            item-title="Label"
            item-value="Id"
            label="Rooftop Unit End Use"
            variant="outlined"
            density="compact"
            hide-details="auto"
            data-testid="rtu-end-use"
          />
        </v-col>
        <v-col cols="12" sm="6" class="d-flex justify-end align-center ga-2">
          <v-btn
            variant="text"
            density="comfortable"
            prepend-icon="mdi-arrow-down-bold-outline"
            data-testid="rtu-copy-latest-hour"
            @click="copyLatestHourDown(rtu)"
          >
            Copy latest hour down
          </v-btn>
          <v-btn
            variant="text"
            density="comfortable"
            prepend-icon="mdi-eraser"
            data-testid="rtu-clear-hours"
            @click="clearHours(rtu)"
          >
            Clear
          </v-btn>
          <v-btn
            :icon="isExpanded(rtu) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            variant="text"
            density="comfortable"
            :aria-label="isExpanded(rtu) ? 'Collapse hours' : 'Expand hours'"
            data-testid="rtu-toggle-hours"
            @click="toggleHours(rtu)"
          />
          <v-btn
            icon="mdi-close"
            variant="text"
            density="comfortable"
            aria-label="Remove rooftop unit"
            data-testid="rtu-remove"
            @click="removeRtu(rtu)"
          />
        </v-col>
      </v-row>

      <v-table
        v-if="isExpanded(rtu)"
        density="compact"
        fixed-header
        height="440"
        class="mt-3"
      >
        <thead>
          <tr>
            <th class="text-left">Hour</th>
            <th class="text-left">kW</th>
            <th class="text-left">50% Swings</th>
            <th class="text-left">Runtime (min)</th>
            <th class="text-left">Starts</th>
            <th class="text-left">Shutdowns</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="hour in hourRows"
            :key="hour.offset"
            data-testid="rtu-hour-row"
          >
            <td class="text-no-wrap">
              <span data-testid="rtu-hour-label">{{ hour.Label }}</span>
              <span
                v-if="hour.offset === 0"
                class="text-caption text-medium-emphasis ml-1"
              >
                (latest)
              </span>
            </td>
            <td>
              <v-text-field
                v-model="rtu.hours[hour.offset].kw"
                :rules="[isDecimalOrBlank]"
                variant="outlined"
                density="compact"
                hide-details="auto"
                data-testid="rtu-kw"
              />
            </td>
            <td>
              <v-text-field
                v-model="rtu.hours[hour.offset].swingCount"
                :rules="[isCountOrBlank]"
                variant="outlined"
                density="compact"
                hide-details="auto"
                data-testid="rtu-swing-count"
              />
            </td>
            <td>
              <v-text-field
                v-model="rtu.hours[hour.offset].runtimeInMinutes"
                :rules="[isCountOrBlank]"
                variant="outlined"
                density="compact"
                hide-details="auto"
                data-testid="rtu-runtime"
              />
            </td>
            <td>
              <v-text-field
                v-model="rtu.hours[hour.offset].startCount"
                :rules="[isCountOrBlank]"
                variant="outlined"
                density="compact"
                hide-details="auto"
                data-testid="rtu-start-count"
              />
            </td>
            <td>
              <v-text-field
                v-model="rtu.hours[hour.offset].shutdownCount"
                :rules="[isCountOrBlank]"
                variant="outlined"
                density="compact"
                hide-details="auto"
                data-testid="rtu-shutdown-count"
              />
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-sheet>

    <div class="d-flex mt-4">
      <v-btn
        color="primary"
        variant="tonal"
        density="comfortable"
        prepend-icon="mdi-plus"
        data-testid="add-rtu"
        :disabled="!canAddRtu"
        @click="addRtu"
      >
        Add rooftop unit
      </v-btn>
    </div>
  </v-sheet>
</template>
<script>
import {
  isNumber,
  isNumberGreaterThanZero,
  hasValue,
  isDate,
} from '@/Core.Patterns/Factory/validationFactory.ts';
import BusinessHoursInput from '@/Core.Service.Domain/Strategies/Strategies/Components/BusinessHoursInput.vue';
import { withBaseInputDefaults } from '@/Core.Service.Domain/Strategies/Strategies/Utils/baseInputDefaults.ts';

// RooftopUnitV1 measures an RTU through an End Use, and it reads them out of a
// single kW pick list (see its GetDataSourceNames: Contacts and EndUsesKw).
const END_USE_DATA_SOURCE = 'EndUsesKw';

// The input looks back 24 hours -- TotalRuntimeFor24Hours and friends -- so the
// grid offers exactly the window the rule methods can reach. Row 0 is the
// latest hour, the one the EndUsesX methods read; row 23 is 23 hours before it.
const HOURS_IN_A_DAY = 24;

// One entry per numeric column of RtuHourlyStatus. `Key` is the row field,
// `Property` the name the record deserialises from, and `Whole` marks the ones
// typed as int on the Core side -- those have to travel as integers or
// Newtonsoft refuses to build the record.
const HOURLY_VALUES = [
  { Key: 'kw', Property: 'ReadingValueKw', Whole: false },
  { Key: 'swingCount', Property: 'SwingCount', Whole: true },
  { Key: 'runtimeInMinutes', Property: 'RuntimeInMinutes', Whole: true },
  { Key: 'startCount', Property: 'StartCount', Whole: true },
  { Key: 'shutdownCount', Property: 'ShutdownCount', Whole: true },
];

export default {
  name: 'RooftopUnitV1Input',

  components: { BusinessHoursInput },

  props: {
    InputData: {
      type: Object,
      required: true,
    },
    DataSources: {
      type: Array,
      required: true,
    },
  },

  data() {
    const localInputData = withBaseInputDefaults(this.InputData);
    const anchor = toAnchorHour(localInputData.ReadingDateTime);
    const recorded = Array.isArray(this.InputData?.HourlyStatuses)
      ? this.InputData.HourlyStatuses
      : [];

    // A recorded run carries one row per End Use per hour, and the hours it
    // holds are whatever the stored procedure looked back on (12 at the time of
    // writing) -- fewer than the 24 the grid shows. Rows land on the offset
    // their timestamp works out to; anything outside the window rides along
    // untouched in `extraStatuses` rather than being quietly dropped.
    const rtus = new Map();
    const extraStatuses = [];
    recorded.forEach((status) => {
      const offset = toHourOffset(status?.ReadingDateTime, anchor);
      if (offset === null || !hasValue(status?.EndUseId)) {
        extraStatuses.push(status);
        return;
      }

      const endUseId = Number(status.EndUseId);
      if (!rtus.has(endUseId)) {
        rtus.set(endUseId, newRtu(rtus.size, endUseId));
      }

      const hour = rtus.get(endUseId).hours[offset];
      HOURLY_VALUES.forEach(({ Key, Property }) => {
        hour[Key] = hasValue(status[Property]) ? String(status[Property]) : '';
      });
      hour.temperature = hasValue(status.Temperature)
        ? Number(status.Temperature)
        : null;
    });

    return {
      localInputData,
      rtus: [...rtus.values()],
      extraStatuses,
      nextRtuKey: rtus.size,
      // Which grids are folded away. Kept out of `rtus` so that folding one --
      // pure display state -- does not travel through the deep watcher and
      // re-emit an identical model.
      collapsedRtuKeys: [],
    };
  },

  computed: {
    // The kW End Uses the site exposes, which is what an RTU is measured
    // through. Items carry only { Id, Name }.
    endUses() {
      return (
        this.DataSources?.find((ds) => ds.Key === END_USE_DATA_SOURCE)?.Value ??
        []
      )
        .map((endUse) => ({
          Id: endUse.Id,
          Name: endUse.Name,
          Label: endUse.Name,
        }))
        .sort(byLabel);
    },

    // Hours recorded against an End Use the site no longer exposes still have
    // to be visible (and editable) when replaying a logged execution. Unlike
    // EnergyEndUsePowerV4, this input records no names alongside its values, so
    // an End Use that has gone away can only be shown by its id.
    endUseOptions() {
      const known = new Set(this.endUses.map((endUse) => endUse.Id));
      const orphans = this.rtus
        .map((rtu) => rtu.endUseId)
        .filter((id) => hasValue(id) && !known.has(id))
        .map((id) => ({
          Id: id,
          Name: `Rooftop Unit #${id}`,
          Label: `Rooftop Unit #${id}`,
        }));

      return [...this.endUses, ...orphans];
    },

    endUseOptionsById() {
      return new Map(this.endUseOptions.map((endUse) => [endUse.Id, endUse]));
    },

    unusedEndUses() {
      const used = new Set(
        this.rtus.map((rtu) => rtu.endUseId).filter((id) => hasValue(id))
      );
      return this.endUses.filter((endUse) => !used.has(endUse.Id));
    },

    canAddRtu() {
      return this.unusedEndUses.length > 0;
    },

    // An End Use can only be entered once, so each panel is offered the ones
    // still free plus its own current selection.
    optionsByRtuKey() {
      return new Map(
        this.rtus.map((rtu) => {
          const options = hasValue(rtu.endUseId)
            ? [...this.unusedEndUses, this.endUseOptionsById.get(rtu.endUseId)]
                .filter(Boolean)
            : this.unusedEndUses;
          return [rtu.key, [...options].sort(byLabel)];
        })
      );
    },

    // The hour every row stands for, counted back from Reading Date/Time --
    // which is the hour the input treats as its latest. Editing that field
    // re-stamps the whole grid, values and all, because a row is held by its
    // offset rather than by its timestamp.
    hourRows() {
      const anchor = toAnchorHour(this.localInputData?.ReadingDateTime);

      return Array.from({ length: HOURS_IN_A_DAY }, (_, offset) => {
        if (anchor === null) {
          return { offset, Stamp: null, Label: `-${offset} h` };
        }

        const hour = new Date(anchor.getTime());
        hour.setHours(hour.getHours() - offset);
        return {
          offset,
          Stamp: toHourStamp(hour),
          Label: toHourLabel(hour, anchor),
        };
      });
    },
  },

  watch: {
    localInputData: {
      handler() {
        this.emitModel();
      },
      deep: true,
    },

    rtus: {
      handler() {
        this.emitModel();
      },
      deep: true,
    },
  },

  methods: {
    isNumber,
    isNumberGreaterThanZero,
    hasValue,
    isDate,

    // Every grid field may be left blank -- a blank hour is sent as 0, the same
    // value the input would read for an hour it has no row for -- so the shared
    // isNumber rule, which counts blank as invalid, would paint the whole grid
    // red before a single value was typed.
    isDecimalOrBlank(value) {
      return !hasValue(value) || isNumber(value);
    },

    // Swings, runtime minutes, starts and shutdowns are int on RtuHourlyStatus.
    isCountOrBlank(value) {
      return (
        !hasValue(value) ||
        (isNumber(value) && Number.isInteger(Number(value)) && Number(value) >= 0)
      );
    },

    addRtu() {
      this.rtus.push(newRtu(this.nextRtuKey++, null));
    },

    removeRtu(rtu) {
      this.rtus = this.rtus.filter((r) => r.key !== rtu.key);
    },

    optionsForRtu(rtu) {
      return this.optionsByRtuKey.get(rtu.key) ?? [];
    },

    isExpanded(rtu) {
      return !this.collapsedRtuKeys.includes(rtu.key);
    },

    // A folded grid is unmounted rather than hidden -- 24 hours x 5 fields is
    // 120 inputs per unit, and the values live in `rtus`, not in the DOM.
    toggleHours(rtu) {
      this.collapsedRtuKeys = this.isExpanded(rtu)
        ? [...this.collapsedRtuKeys, rtu.key]
        : this.collapsedRtuKeys.filter((key) => key !== rtu.key);
    },

    // 24 hours x 5 values is 120 fields per unit, and a test run usually wants
    // the same reading repeated across the window (an RTU that never shut down,
    // a unit stuck at one kW). The latest hour is the one the EndUsesX methods
    // read, so it is the one worth spreading.
    copyLatestHourDown(rtu) {
      const [latest] = rtu.hours;
      rtu.hours.forEach((hour, offset) => {
        if (offset === 0) return;

        HOURLY_VALUES.forEach(({ Key }) => {
          hour[Key] = latest[Key];
        });
      });
    },

    clearHours(rtu) {
      rtu.hours.forEach((hour) => {
        HOURLY_VALUES.forEach(({ Key }) => {
          hour[Key] = '';
        });
      });
    },

    emitModel() {
      // Unlike the End Use editors, which send only the readings that were
      // typed, this one sends all 24 hours of every unit that has an End Use
      // picked: the grid is the payload. A blank field is a 0, which is what
      // the input reads for a missing hour anyway, and keeping the rows whole
      // means the "latest hour" the methods read is always the top row rather
      // than the topmost row that happened to be filled in.
      const hourly = this.rtus
        .filter((rtu) => hasValue(rtu.endUseId))
        .flatMap((rtu) =>
          this.hourRows
            // No Stamp means Reading Date/Time is mid-edit and unparseable.
            // The run is broken until it is valid again, and the values stay in
            // the grid, so there is nothing worth stamping with a guess.
            .filter((hour) => hour.Stamp !== null)
            .map((hour) => this.toHourlyStatus(rtu, hour))
        );

      this.$emit('update:model', {
        ...this.localInputData,
        HourlyStatuses: [...hourly, ...this.extraStatuses],
      });
    },

    toHourlyStatus(rtu, hour) {
      const values = rtu.hours[hour.offset];

      return HOURLY_VALUES.reduce(
        (status, { Key, Property, Whole }) => {
          status[Property] = Whole
            ? toCount(values[Key])
            : toDecimal(values[Key]) ?? 0;
          return status;
        },
        {
          ReadingDateTime: hour.Stamp,
          EndUseId: rtu.endUseId,
          // Only the most recent hour's temperature is ever read (it is what
          // fills the input's own Outside Temperature), but a replayed run
          // arrives with one per row, so a recorded hour keeps the temperature
          // it ran with and a hand-entered one takes the field above.
          Temperature:
            values.temperature ?? toDecimal(this.localInputData.Temperature),
        }
      );
    },
  },
};

// A unit starts with 24 blank hours; the grid always shows all of them.
function newRtu(key, endUseId) {
  return {
    key,
    endUseId,
    hours: Array.from({ length: HOURS_IN_A_DAY }, () =>
      HOURLY_VALUES.reduce((hour, { Key }) => ({ ...hour, [Key]: '' }), {
        temperature: null,
      })
    ),
  };
}

// The fields take free text, so guard what leaves as a reading. Number() is
// used rather than parseFloat() because parseFloat("12abc") silently yields 12
// -- the field would flag it via its rule while the payload carried it anyway.
// Infinity (and "1e999", which overflows to it) is rejected too: the backend
// stores these as decimal.
function toDecimal(value) {
  if (!hasValue(value) || String(value).trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// SwingCount, RuntimeInMinutes, StartCount and ShutdownCount are int, and
// Newtonsoft will not build RtuHourlyStatus out of a fractional number, so a
// value that slipped past the field rule is rounded rather than shipped as is.
function toCount(value) {
  const parsed = toDecimal(value);
  return parsed === null ? 0 : Math.round(parsed);
}

// `datetime-local` hands back local wall-clock time, and so does a recorded
// run, so both parse as local. The hour is the unit the input works in -- the
// stored procedure returns one row per hour -- so the minutes are dropped.
function toAnchorHour(readingDateTime) {
  if (!hasValue(readingDateTime)) {
    return null;
  }

  const anchor = new Date(readingDateTime);
  if (Number.isNaN(anchor.getTime())) {
    return null;
  }

  anchor.setMinutes(0, 0, 0);
  return anchor;
}

// Which grid row a recorded status belongs on, or null when it falls outside
// the 24-hour window the grid covers.
function toHourOffset(readingDateTime, anchor) {
  if (anchor === null || !hasValue(readingDateTime)) {
    return null;
  }

  const hour = new Date(readingDateTime);
  if (Number.isNaN(hour.getTime())) {
    return null;
  }

  const offset = Math.round(
    (anchor.getTime() - hour.getTime()) / (60 * 60 * 1000)
  );
  return offset >= 0 && offset < HOURS_IN_A_DAY ? offset : null;
}

const pad = (part) => String(part).padStart(2, '0');

// RtuHourlyStatus.ReadingDateTime is a DateTime, so it travels as local
// wall-clock time -- the same shape the base input's timestamps use.
function toHourStamp(hour) {
  return (
    `${hour.getFullYear()}-${pad(hour.getMonth() + 1)}-${pad(hour.getDate())}` +
    `T${pad(hour.getHours())}:00:00`
  );
}

// The window walks back over midnight, so the hours before it carry their date.
function toHourLabel(hour, anchor) {
  const sameDay =
    hour.getFullYear() === anchor.getFullYear() &&
    hour.getMonth() === anchor.getMonth() &&
    hour.getDate() === anchor.getDate();

  return sameDay
    ? `${pad(hour.getHours())}:00`
    : `${pad(hour.getMonth() + 1)}/${pad(hour.getDate())} ${pad(hour.getHours())}:00`;
}

function byLabel(a, b) {
  return a.Label.localeCompare(b.Label);
}
</script>
